/**
 * Pinned answers (ADMIN-266): a chat answer kept on the board as a widget. Declarative, not code:
 * the widget's config says where its content comes from and how often it refreshes, and its
 * content (the answer's markdown, and a visual in the same format chat answers draw) is stored
 * like any widget's content (`home_widget_data`). Nothing in it runs, so it's safe to share.
 */

export const ANSWER_WIDGET_TYPE = "kourai.answer";

/** How often a pinned answer asks again, in hours (0: never, it stays as pinned). */
export const ANSWER_REFRESH_HOURS = [0, 1, 6, 24] as const;

/** The longest prompt a pinned answer keeps. */
export const ANSWER_PROMPT_MAX = 2000;

/**
 * What a pinned answer's config holds: its source and schedule. The widget's schema lives with
 * the widget (packages/extension/src/widgets/answer), built from these limits.
 */
export type AnswerConfig = {
  /** "prompt" for now; a fetch, a page or an MCP tool later. */
  source: "prompt";
  prompt: string;
  /** The model it was asked with, so a refresh answers in the same voice. */
  model: string;
  refreshHours: (typeof ANSWER_REFRESH_HOURS)[number];
};

/** A pinned answer's content: markdown, an optional visual, and when it was answered. */
export type AnswerContent = {
  markdown: string;
  /** A `show_visual` input (table, bar, line, pie), drawn by the same renderer as in chat. */
  visual?: unknown;
  answeredAt: string;
};

/** Content is cut to this, so it fits a widget's 64 KB with room for a visual. */
export const ANSWER_MARKDOWN_MAX = 24_000;

/** A refresh asks the model again at most this often, whatever the widget's schedule says. */
export const ANSWER_MIN_REFRESH_MS = 60 * 60 * 1000;

export const ANSWER_INSTRUCTIONS = `You keep one tile on someone's home page up to date.
Answer the request below again, as of now, for that tile: short markdown, at most about 150 words,
lists over paragraphs, no greeting, no preamble, no follow-up questions. If you can't know
something current (you have no web access here), say so in one line instead of guessing.`;
